import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faTimes } from '@fortawesome/free-solid-svg-icons';

// Grade colors from your Constants
const gradeColors = {
  1: "rgba(255, 11, 11, 1)",
  2: "rgba(255, 11, 11, 0.85)",
  3: "rgba(255, 11, 11, 0.5)",
  4: "rgba(255, 11, 11, 0.3)",
  5: "rgba(255, 151, 11, 0.4)",
  6: "rgba(255, 151, 11, 0.3)",
  7: "rgba(11, 255, 11, 0.3)",
  8: "rgba(11, 255, 11, 0.5)",
  9: "rgba(11, 255, 11, 0.75)",
  10: "rgba(11, 255, 11, 1)"
};

// Rating labels mapping
const ratingLabels = {
  rating: 'Overall',
  conceptrating: 'Concept',
  plotrating: 'Plot',
  charactersrating: 'Characters',
  emotional_impact: 'Emotional Impact',
  conflict_level: 'Conflict Level',
  dialoguerating: 'Dialogue',
  storyforward: 'Story Forward',
  characterchanges: 'Character Changes',
  highstakes: 'High Stakes',
  internal_goal_score: 'Internal Goal',
  external_goal_score: 'External Goal',
  originality_score: 'Originality',
  engagement_score: 'Engagement',
  pacing_score: 'Pacing',
  formatting_score: 'Formatting',
  structure_score: 'Structure',
  themerating: 'Theme',
  philosophical_conflict_score: 'Philosophical Conflict',
  unpredictability_score: 'Unpredictability'
};

const ratingKeys = Object.keys(ratingLabels);

/**
 * Helper function to format numbers consistently
 */
const formatRating = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const num = parseFloat(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  // Format to 1 decimal place, then remove trailing .0
  return num.toFixed(1).replace(/\.0$/, '');
};

/**
 * Helper function to format date for display
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Helper function to get relative time (e.g., "2 minutes ago", "3 days ago")
 */
const getRelativeTime = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSeconds < 60) {
    return diffInSeconds === 1 ? '1 second ago' : `${diffInSeconds} seconds ago`;
  } else if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
  } else if (diffInHours < 24) {
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  } else if (diffInDays < 7) {
    return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
  } else if (diffInWeeks < 4) {
    return diffInWeeks === 1 ? '1 week ago' : `${diffInWeeks} weeks ago`;
  } else if (diffInMonths < 12) {
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  } else {
    return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
  }
};

/**
 * Helper function to safely convert various data types to array
 */
const safeArrayConvert = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed;
      return [data]; // If it's a string that's not JSON array, wrap it
    } catch (e) {
      return [data]; // If JSON parsing fails, wrap the string
    }
  }
  // If it's an object or other type, try to convert to array
  if (typeof data === 'object') {
    return Object.values(data).filter(Boolean);
  }
  return []; // Default fallback
};

const SceneEvaluationsModal = ({ 
  isOpen, 
  onClose, 
  evaluations = [],
  workspaceTitle 
}) => {
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [isSceneTextExpanded, setIsSceneTextExpanded] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [abbreviateHeaders, setAbbreviateHeaders] = useState(false);
  const detailsSectionRef = useRef(null);

  // Auto-select the first (most recent) evaluation when modal opens
  useEffect(() => {
    if (isOpen && evaluations.length > 0 && !selectedEvaluation) {
      setSelectedEvaluation(evaluations[0]);
    }
  }, [isOpen, evaluations, selectedEvaluation]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedEvaluation(null);
      setIsSceneTextExpanded(false);
      setHoveredCell(null);
      setAbbreviateHeaders(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Calculate best scores for purple highlighting
  const bestPerKey = {};
  ratingKeys.forEach(k => {
    bestPerKey[k] = Math.max(
      ...evaluations.map(evaluation => {
        const num = parseFloat(evaluation.ratings?.[k]);
        return Number.isFinite(num) ? num : -Infinity;
      })
    );
  });

  const handleModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDateClick = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setIsSceneTextExpanded(false); // Reset scene text to collapsed when switching
    
    // Scroll to details section after a brief delay to ensure state updates
    setTimeout(() => {
      if (detailsSectionRef.current) {
        detailsSectionRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const handleCellMouseEnter = (evaluationId, criterionKey) => {
    setHoveredCell({ evaluationId, criterionKey });
  };

  const handleCellMouseLeave = () => {
    setHoveredCell(null);
  };

  // Get explanation for a hovered cell
  const getExplanation = (evaluation, criterionKey) => {
    if (!evaluation || !evaluation.ratings) return '';
    const explanationKey = `${criterionKey}_explanation`;
    return evaluation.ratings[explanationKey] || '';
  };

  return (
    <div
      className="modal show"
      style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      onClick={handleModalBackdropClick}
    >
      <div className="modal-dialog modal-xl" style={{ marginTop: '5vh', maxHeight: '90vh' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Scene Evaluations - {workspaceTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          
          {/* Disclaimer and Controls */}
          <div className="alert alert-info mx-3 mt-3 mb-0">
            <small>
              <strong>Note:</strong> These evaluations use a different AI model than regular scene grading, 
              so ratings may vary from other grading systems.
            </small>
          </div>
          
          <div className="px-3 py-2 border-bottom">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="abbreviateHeaders"
                checked={abbreviateHeaders}
                onChange={(e) => setAbbreviateHeaders(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="abbreviateHeaders">
                <small>Abbreviate column headers</small>
              </label>
            </div>
          </div>
          
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {evaluations.length > 0 ? (
              <>
                {/* Evaluations Table */}
                <div className="table-responsive mb-4" style={{ overflow: 'visible' }}>
                  <table className="table table-bordered table-sm text-center align-middle" style={{ overflow: 'visible' }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: 200 }}>
                          <div className="d-flex align-items-center">
                            <span>Date Generated</span>
                            <small className="text-muted ms-2">(click to view details)</small>
                          </div>
                        </th>
                        {ratingKeys.map(k => (
                          <th key={k} title={abbreviateHeaders ? ratingLabels[k] : undefined}>
                            {abbreviateHeaders ? ratingLabels[k].substring(0, 2) : ratingLabels[k]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody style={{ overflow: 'visible' }}>
                      {evaluations.map((evaluation) => (
                        <tr key={evaluation.id} style={{ overflow: 'visible' }}>
                          <td 
                            className="text-start"
                            style={{ 
                              cursor: 'pointer',
                              backgroundColor: selectedEvaluation?.id === evaluation.id ? '#e3f2fd' : 'transparent',
                              padding: '12px',
                              borderLeft: selectedEvaluation?.id === evaluation.id ? '4px solid #2196f3' : '4px solid transparent',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => handleDateClick(evaluation)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = selectedEvaluation?.id === evaluation.id ? '#e3f2fd' : '#f5f5f5';
                              e.currentTarget.style.borderLeftColor = '#2196f3';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = selectedEvaluation?.id === evaluation.id ? '#e3f2fd' : 'transparent';
                              e.currentTarget.style.borderLeftColor = selectedEvaluation?.id === evaluation.id ? '#2196f3' : 'transparent';
                            }}
                            title={`Click to view details • Full date: ${formatDate(evaluation.created_at)}`}
                          >
                            <div style={{ 
                              color: '#1976d2', 
                              fontWeight: '600',
                              fontSize: '14px',
                              marginBottom: '2px'
                            }}>
                              {getRelativeTime(evaluation.created_at)}
                            </div>
                            <div style={{ 
                              color: '#666', 
                              fontSize: '12px',
                              opacity: 0.8
                            }}>
                              {formatDate(evaluation.created_at)}
                            </div>
                          </td>
                          {ratingKeys.map(k => {
                            const rawValue = evaluation.ratings?.[k];
                            const formattedValue = formatRating(rawValue);
                            const numValue = parseFloat(rawValue);
                            const isValidNumber = Number.isFinite(numValue);
                            const bg = isValidNumber ? gradeColors[Math.round(numValue)] : 'transparent';
                            const isBest = isValidNumber && numValue === bestPerKey[k];
                            const isHovered = hoveredCell?.evaluationId === evaluation.id && hoveredCell?.criterionKey === k;
                            const explanation = getExplanation(evaluation, k);

                            return (
                              <td 
                                key={k} 
                                style={{ 
                                  backgroundColor: bg,
                                  position: 'relative',
                                  cursor: explanation ? 'help' : 'default',
                                  overflow: 'visible'
                                }}
                                onMouseEnter={() => explanation && handleCellMouseEnter(evaluation.id, k)}
                                onMouseLeave={handleCellMouseLeave}
                              >
                                {isBest ? (
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 6px',
                                    borderRadius: '50%',
                                    border: '2px solid purple',
                                    fontWeight: 'bold'
                                  }}>
                                    {formattedValue}
                                  </span>
                                ) : (
                                  formattedValue
                                )}
                                
                                {/* Tooltip for explanation */}
                                {isHovered && explanation && (
                                  <div
                                    style={{
                                      position: 'fixed',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                      color: 'white',
                                      padding: '12px 16px',
                                      borderRadius: '6px',
                                      zIndex: 9999,
                                      maxWidth: '300px',
                                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                      fontSize: '13px',
                                      lineHeight: '1.4',
                                      whiteSpace: 'normal',
                                      textAlign: 'left'
                                    }}
                                  >
                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                      {ratingLabels[k]} ({formattedValue}/10)
                                    </div>
                                    {explanation}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Selected Evaluation Details */}
                {selectedEvaluation && (
                  <div className="border-top pt-4" ref={detailsSectionRef}>
                    <h6 className="mb-3">
                      Evaluation from {getRelativeTime(selectedEvaluation.created_at)} 
                      <small className="text-muted ms-2">({formatDate(selectedEvaluation.created_at)})</small>
                    </h6>

                    {/* Strengths, Weaknesses, Suggestions */}
                    <div className="row mb-4">
                      {/* Strengths */}
                      <div className="col-md-4">
                        <div className="card h-100">
                          <div className="card-header bg-success text-white">
                            <h6 className="mb-0">Strengths</h6>
                          </div>
                          <div className="card-body">
                            {(() => {
                              const strengths = safeArrayConvert(selectedEvaluation.strengths);
                              return strengths.length > 0 ? (
                                <ul className="mb-0">
                                  {strengths.map((strength, idx) => (
                                    <li key={idx}>{strength}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-muted mb-0">No strengths listed</p>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Weaknesses */}
                      <div className="col-md-4">
                        <div className="card h-100">
                          <div className="card-header bg-warning text-dark">
                            <h6 className="mb-0">Weaknesses</h6>
                          </div>
                          <div className="card-body">
                            {(() => {
                              const weaknesses = safeArrayConvert(selectedEvaluation.weaknesses);
                              return weaknesses.length > 0 ? (
                                <ul className="mb-0">
                                  {weaknesses.map((weakness, idx) => (
                                    <li key={idx}>{weakness}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-muted mb-0">No weaknesses listed</p>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Suggestions */}
                      <div className="col-md-4">
                        <div className="card h-100">
                          <div className="card-header bg-primary text-white">
                            <h6 className="mb-0">Suggestions</h6>
                          </div>
                          <div className="card-body">
                            {(() => {
                              const suggestions = safeArrayConvert(selectedEvaluation.suggestions);
                              return suggestions.length > 0 ? (
                                <ul className="mb-0">
                                  {suggestions.map((suggestion, idx) => (
                                    <li key={idx}>{suggestion}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-muted mb-0">No suggestions listed</p>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Critique Section */}
                    {(() => {
                      const critique = safeArrayConvert(selectedEvaluation.critique);
                      return critique.length > 0 && (
                        <div className="mb-4">
                          <div className="card">
                            <div className="card-header bg-info text-white">
                              <h6 className="mb-0">Critique</h6>
                            </div>
                            <div className="card-body">
                              <ul className="mb-0">
                                {critique.map((note, idx) => (
                                  <li key={idx}>{note}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Detailed Scores Table */}
                    <div className="mb-4">
                      <h6>Detailed Scores</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Criterion</th>
                              <th style={{ width: '80px' }}>Score</th>
                              <th>Explanation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ratingKeys.map(key => {
                              const score = selectedEvaluation.ratings?.[key];
                              const explanation = selectedEvaluation.ratings?.[`${key}_explanation`] || '';
                              const formattedScore = formatRating(score);
                              const numValue = parseFloat(score);
                              const isValidNumber = Number.isFinite(numValue);
                              const bg = isValidNumber ? gradeColors[Math.round(numValue)] : 'transparent';

                              return (
                                <tr key={key}>
                                  <td><strong>{ratingLabels[key]}</strong></td>
                                  <td 
                                    className="text-center"
                                    style={{ backgroundColor: bg }}
                                  >
                                    {formattedScore}
                                  </td>
                                  <td>{explanation || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Scene Text (Collapsible) */}
                    <div className="card">
                      <div 
                        className="card-header d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsSceneTextExpanded(!isSceneTextExpanded)}
                      >
                        <h6 className="mb-0">Scene Text</h6>
                        <FontAwesomeIcon 
                          icon={isSceneTextExpanded ? faChevronUp : faChevronDown} 
                        />
                      </div>
                      {isSceneTextExpanded && (
                        <div className="card-body">
                          <pre 
                            style={{ 
                              whiteSpace: 'pre-wrap', 
                              backgroundColor: '#f8f9fa',
                              padding: '15px',
                              borderRadius: '4px',
                              maxHeight: '400px',
                              overflowY: 'auto'
                            }}
                          >
                            {selectedEvaluation.scene_text || 'No scene text available'}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">No evaluations found for this workspace.</p>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

SceneEvaluationsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  evaluations: PropTypes.array,
  workspaceTitle: PropTypes.string
};

export default SceneEvaluationsModal;